import { useEffect } from 'react'

import Modal from 'react-modal'
import { toast } from 'react-toastify'

import { ResultCode, StepNames } from '@src/enums'
import FeatherSwapInProgressFlow from '@src/pages/Exchange/components/FeatherOrderFlow/FeatherSwapInProgressFlow'
import { IExchangeData } from '@src/types/exchange'
import { IFee } from '@src/types/fee'
import { Address } from '@src/types/token'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

interface IFeatherFlowProps {
    isOpen: boolean
    onClose: () => void
    sellData: IExchangeData
    buyData: IExchangeData
    fees: IFee[]
    depositAddress: Address
    miles?: number
}

export default function FeatherFlow({
    isOpen,
    onClose,
    sellData,
    buyData,
    fees,
    depositAddress,
    miles,
}: IFeatherFlowProps) {
    // Get application and history stores
    const { exchangeId } = useApplicationStore()
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const historyState = useHistoryStore(state => state.getOrder(exchangeId))

    // Handle confirmation after signing the quote
    const handleConfirm = async () => {
        try {
            console.log('⚡️ FeatherFlow handleConfirm called')

            // Validate that this is a Feather order with quote details
            if (!sellData.isFeatherOrder || !sellData.featherQuoteId) {
                console.error('❌ No valid Feather quote found', { sellData })
                toast.error('No valid Feather quote found')
                return
            }

            console.log('⚡️ FeatherFlow: Updating history with FEATHER_WAITING_FOR_DEPOSIT')

            // Update the history to reflect the transaction deposit step
            updateHistory(exchangeId, {
                currentStep: StepNames.FEATHER_WAITING_FOR_DEPOSIT,
                result: {
                    status: ResultCode.pending,
                    message: `Please send funds to the displayed deposit address`,
                },
            })

            console.log('⚡️ handleConfirm: Updated step to FEATHER_WAITING_FOR_DEPOSIT')
        } catch (error) {
            console.error('❌ Error handling confirmation:', error)
            toast.error('Failed to process confirmation')
        }
    }

    // Handle reset flow when a user clicks "New Swap" from the completed or failed screens
    const handleReset = () => {
        // Reset the history state
        updateHistory(exchangeId, {
            currentStep: StepNames.UNSTARTED,
            result: undefined,
            orderId: undefined,
        })

        // Close the modal
        onClose()

        // Optional: Show a toast message
        toast.info('Ready for a new swap!', {
            position: 'bottom-right',
            autoClose: 2000,
        })
    }

    // Get current step and result from history state
    const currentStep = historyState?.currentStep || StepNames.UNSTARTED
    const result = historyState?.result
    const orderId = historyState?.orderId

    return (
        <FeatherSwapInProgressFlow
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={handleConfirm}
            currentStep={currentStep}
            result={result}
            fees={fees}
            depositAddress={depositAddress}
            miles={miles}
            orderId={orderId}
        />
    )
}
